import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "en" | "fr" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    dashboard: "Dashboard",
    containers: "Containers",
    menu: "Menu",
    ingredients: "Ingredients",
    inventory: "Inventory",
    analytics: "Analytics",
    dishAnalytics: "Dish Analytics",
    leaderboard: "Leaderboard",
    affiliates: "Affiliates",
    team: "Team",
    recipes: "Recipes",
    businessSetup: "Business",
    subscription: "Subscription",
    settings: "Settings",
    signOut: "Sign Out",
    
    // Settings Page
    profile: "Profile",
    integrations: "Integrations",
    notifications: "Notifications",
    security: "Security",
    preferences: "Preferences",
    language: "Language",
    theme: "Theme",
    lightMode: "Light",
    darkMode: "Dark",
    systemMode: "System",
    english: "English",
    french: "Français",
    spanish: "Español",
    manageAccountPreferences: "Manage your account and preferences",
    profileInformation: "Profile Information",
    updatePersonalDetails: "Update your personal details",
    fullName: "Full Name",
    yourName: "Your name",
    email: "Email",
    emailCannotChange: "Email cannot be changed directly",
    saveChanges: "Save Changes",
    saving: "Saving...",
    appearanceLanguage: "Appearance & Language",
    customizeExperience: "Customize your app experience",
    externalIntegrations: "External Integrations",
    connectRestaurantSystems: "Connect BacSense with your restaurant management systems for automated data sync",
    restaurantManagementPos: "Restaurant management & POS",
    notConnected: "Not Connected",
    apiKey: "API Key",
    enterApiKey: "Enter your {service} API key",
    salesOrderSync: "Sales and order data sync",
    restaurantManagementPlatform: "Restaurant management platform",
    deliveryOrderTracking: "Delivery order tracking",
    saveIntegrationSettings: "Save Integration Settings",
    manageNotificationPreferences: "Manage your notification preferences",
    dailyReminders: "Daily Reminders",
    morningEveningReminders: "Morning and evening logging reminders",
    wasteAlerts: "Waste Alerts",
    wasteExceedsTargets: "Get notified when waste exceeds targets",
    competitionUpdates: "Competition Updates",
    teamLeaderboardChanges: "Team leaderboard and ranking changes",
    affiliateNews: "Affiliate News",
    referralCommissionUpdates: "Updates about your referrals and commissions",
    manageAccountSecurity: "Manage your account security",
    changePassword: "Change Password",
    profileUpdatedSuccess: "Profile updated successfully!",
    notificationPreferencesUpdated: "Notification preferences updated",
    
    // Index/Landing Page
    appTitle: "BacSense",
    appDescription: "AI-powered food waste tracking and analytics for restaurants",
    getStarted: "Get Started",
    
    // Auth Page
    signIn: "Sign In",
    signUp: "Sign Up",
    createAccount: "Create Account",
    signingIn: "Signing in...",
    creatingAccount: "Creating account...",
    accountCreated: "Account created! You can now log in.",
    trackInventory: "Track inventory, reduce waste, optimize your kitchen",
    password: "Password",
    fullNameLabel: "Full Name",
    
    // Affiliates Page
    affiliateProgram: "Affiliate Program",
    inviteRestaurants: "Invite restaurants and earn rewards for every successful referral",
    totalReferrals: "Total Referrals",
    totalEarnings: "Total Earnings",
    pending: "Pending",
    awaitingSignup: "Awaiting signup",
    commissionEarned: "Commission earned",
    inviteRestaurant: "Invite a Restaurant",
    sendInvitationEarn: "Send an invitation and earn commission when they join",
    restaurantEmail: "Restaurant Email",
    commissionStructure: "Commission Structure",
    firstYearCommission: "20% of first year subscription",
    recurringCommission: "10% recurring for lifetime",
    bonusActive: "Bonus for active restaurants",
    sendInvitation: "Send Invitation",
    yourReferrals: "Your Referrals",
    trackInvitedRestaurants: "Track your invited restaurants",
    noReferralsYet: "No referrals yet",
    startInviting: "Start inviting restaurants to earn rewards!",
    invited: "Invited",
    earned: "earned",
    pleaseEnterEmail: "Please enter an email address",
    invitationSent: "Invitation sent successfully!",
    active: "active",
    
    // Dashboard Page
    analyticsDashboard: "Analytics Dashboard",
    comprehensiveInsights: "Comprehensive insights into your daily inventory cycle and platform performance",
    avgDailyWaste: "Avg Daily Waste",
    accuracy: "Accuracy",
    monthlySavings: "Monthly Savings",
    efficiency: "Efficiency",
    activeItems: "Active Items",
    wasteReduction: "Waste Reduction",
    vsLastWeek: "vs last week",
    improvement: "improvement",
    growth: "growth",
    thisWeek: "this week",
    fromPrepared: "from prepared",
    dailyInventoryCycle: "Daily Inventory Cycle",
    preparedRemainingWaste: "Prepared, remaining, and waste over the last 7 days",
    noCycleData: "No cycle data available yet",
    categoryBreakdown: "Category Breakdown",
    wasteByCategory: "Waste distribution and efficiency across food categories",
    noCategoryData: "No category data available",
    hourlyActivity: "Hourly Activity",
    loggingPatterns: "Logging patterns throughout the day",
    noActivityData: "No activity data available",
    inventoryByCategory: "Inventory by Category",
    currentStockLevels: "Current stock levels across different categories",
    noInventoryData: "No inventory data available",
    costImpact: "Cost Impact Analysis",
    savingsByCategory: "Potential savings and costs by category",
    noCostData: "No cost data available",
    recentActivity: "Recent Activity",
    latestContainerActions: "Latest container logs and system activity",
    aiRecommendations: "AI Recommendations",
    personalizedSuggestions: "Personalized suggestions to reduce waste",
    loadingAnalytics: "Loading analytics...",
    failedToLoadAnalytics: "Failed to load analytics",
    
    // Containers Page
    containerManagement: "Container Management",
    threePhaseWorkflow: "Three-phase daily workflow: Morning → In Between → Evening",
    whyThisProcess: "Why This Process?",
    processExplanation: "This workflow eliminates manual counting and guesswork. Instead of wasting time inspecting containers each morning, our system:",
    trackingBenefit: "Tracks starting inventory, all changes, and ending stock using photos, weights, and quick logs",
    aiBenefit: "Uses AI to recognize ingredients automatically—no manual entry needed",
    recommendationsBenefit: "Provides precise cooking recommendations for the next shift based on real consumption data",
    wasteReductionBenefit: "Reduces food waste by matching preparation to actual usage patterns",
    resultBenefit: "Result: Less stress, fewer errors, and data-driven decisions for every meal.",
    startMorning: "Start Morning",
    duringService: "During Service",
    viewGuide: "View Guide",
    startEvening: "Start Evening",
    backToSelection: "← Back to Selection",
    dailyWorkflowComplete: "Daily workflow complete! 🎉",
    failedToLoadContainers: "Failed to load containers",
    
    // Inventory Page
    monitorStockLevels: "Monitor your ingredient stock levels and manage inventory",
    loadingInventory: "Loading inventory...",
    noInventoryItems: "No inventory items found",
    lowStock: "Low Stock",
    inStock: "In Stock",
    currentStock: "Current Stock",
    minStock: "Min Stock",
    lastOrder: "Last Order",
    costTrend: "Cost Trend",
    supplier: "Supplier",
    
    // Menu Management Page
    menuManagement: "Menu Management",
    setupRestaurantMenu: "Set up your restaurant menu and assign ingredients",
    addMenuItem: "Add Menu Item",
    editMenuItem: "Edit Menu Item",
    addNewMenuItem: "Add New Menu Item",
    enterMenuItemDetails: "Enter the details for your menu item",
    itemName: "Item Name",
    category: "Category",
    description: "Description",
    cancel: "Cancel",
    add: "Add",
    update: "Update",
    item: "Item",
    menuItemsCount: "Menu Items",
    manageMenuItems: "Manage your restaurant's menu items and link them to ingredients",
    loadingMenuItems: "Loading menu items...",
    noMenuItemsYet: "No menu items yet",
    addFirstMenuItem: "Add Your First Menu Item",
    menuItemAdded: "Menu item added successfully!",
    menuItemUpdated: "Menu item updated successfully!",
    menuItemDeleted: "Menu item deleted successfully!",
    confirmDelete: "Are you sure you want to delete this menu item?",
    failedToLoadMenuItems: "Failed to load menu items",
    
    // Subscription Page
    subscriptionTitle: "Subscription",
    manageMembership: "Manage your BacSense Pro membership",
    currentSubscriptionStatus: "Current Subscription Status",
    activeSubscription: "Active Subscription",
    dayTrial: "15-Day Trial",
    noActiveSubscription: "No Active Subscription",
    subscribeToAccess: "Subscribe to access all features",
    trialEnds: "Trial ends:",
    nextBillingDate: "Next billing date:",
    refreshStatus: "Refresh Status",
    manageSubscription: "Manage Subscription",
    subscribeNow: "Subscribe Now",
    opening: "Opening...",
    processing: "Processing...",
    bacSenseProMembership: "BacSense Pro Membership",
    monthlyPrice: "€199/month with 15-day free trial",
    aiRecommendationsFeature: "AI-powered waste reduction recommendations",
    advancedAnalyticsFeature: "Advanced analytics and reporting",
    inventoryManagementFeature: "Comprehensive inventory management",
    recipeOptimizationFeature: "Recipe and menu optimization",
    realtimeTrackingFeature: "Real-time consumption tracking",
    teamCollaborationFeature: "Team collaboration tools",
    subscriptionActivated: "Subscription activated successfully!",
    checkoutCanceled: "Subscription checkout canceled",
    failedCheckout: "Failed to start checkout",
    failedPortal: "Failed to open subscription management",
    
    // 404 Page
    pageNotFound: "Page not found",
    oops: "Oops!",
    returnHome: "Return to Home",
    
    // Container workflow
    morningLog: "Morning Log",
    eveningLog: "Evening Log",
    inBetweenTracking: "In Between Tracking",
    fillAllFields: "Please fill in all required fields",
    
    // Business Setup Page
    businessTitle: "Business",
    configureRestaurantDetails: "Configure your restaurant's operational details for AI-powered insights",
    operatingHours: "Operating Hours",
    setDailySchedule: "Set your daily operating schedule",
    openingTime: "Opening Time",
    closingTime: "Closing Time",
    timezone: "Timezone",
    peakServiceHours: "Peak Service Hours",
    defineBusyPeriods: "Define your busiest meal periods for better AI predictions",
    lunchRush: "Lunch Rush",
    dinnerRush: "Dinner Rush",
    restaurantDetails: "Restaurant Details",
    aboutEstablishment: "Information about your establishment",
    cuisineType: "Cuisine Type",
    cuisinePlaceholder: "e.g., Italian, French, Asian Fusion",
    averageDailyCovers: "Average Daily Covers",
    coversPlaceholder: "e.g., 120",
    typicalCustomers: "Typical number of customers served per day",
    saveSettings: "Save Settings",
    businessSettingsUpdated: "Business settings updated successfully",
  },
  fr: {
    // Navigation
    dashboard: "Tableau de bord",
    containers: "Conteneurs",
    menu: "Menu",
    ingredients: "Ingrédients",
    inventory: "Inventaire",
    analytics: "Analytique",
    dishAnalytics: "Analytique des plats",
    leaderboard: "Classement",
    affiliates: "Affiliés",
    team: "Équipe",
    recipes: "Recettes",
    businessSetup: "Entreprise",
    subscription: "Abonnement",
    settings: "Paramètres",
    signOut: "Déconnexion",
    
    // Settings Page
    profile: "Profil",
    integrations: "Intégrations",
    notifications: "Notifications",
    security: "Sécurité",
    preferences: "Préférences",
    language: "Langue",
    theme: "Thème",
    lightMode: "Clair",
    darkMode: "Sombre",
    systemMode: "Système",
    english: "English",
    french: "Français",
    spanish: "Español",
    manageAccountPreferences: "Gérez votre compte et vos préférences",
    profileInformation: "Informations de profil",
    updatePersonalDetails: "Mettez à jour vos informations personnelles",
    fullName: "Nom complet",
    yourName: "Votre nom",
    email: "Email",
    emailCannotChange: "L'email ne peut pas être modifié directement",
    saveChanges: "Enregistrer les modifications",
    saving: "Enregistrement...",
    appearanceLanguage: "Apparence et langue",
    customizeExperience: "Personnalisez votre expérience",
    externalIntegrations: "Intégrations externes",
    connectRestaurantSystems: "Connectez BacSense à vos systèmes de gestion pour la synchronisation automatique",
    restaurantManagementPos: "Gestion de restaurant et PDV",
    notConnected: "Non connecté",
    apiKey: "Clé API",
    enterApiKey: "Entrez votre clé API {service}",
    salesOrderSync: "Synchronisation des ventes et commandes",
    restaurantManagementPlatform: "Plateforme de gestion de restaurant",
    deliveryOrderTracking: "Suivi des commandes de livraison",
    saveIntegrationSettings: "Enregistrer les paramètres d'intégration",
    manageNotificationPreferences: "Gérez vos préférences de notification",
    dailyReminders: "Rappels quotidiens",
    morningEveningReminders: "Rappels de journalisation matin et soir",
    wasteAlerts: "Alertes de gaspillage",
    wasteExceedsTargets: "Soyez notifié lorsque le gaspillage dépasse les objectifs",
    competitionUpdates: "Mises à jour de compétition",
    teamLeaderboardChanges: "Changements de classement d'équipe",
    affiliateNews: "Nouvelles d'affiliation",
    referralCommissionUpdates: "Mises à jour sur vos parrainages et commissions",
    manageAccountSecurity: "Gérez la sécurité de votre compte",
    changePassword: "Changer le mot de passe",
    profileUpdatedSuccess: "Profil mis à jour avec succès!",
    notificationPreferencesUpdated: "Préférences de notification mises à jour",
    
    // Index/Landing Page
    appTitle: "BacSense",
    appDescription: "Suivi et analyse du gaspillage alimentaire alimentés par l'IA pour les restaurants",
    getStarted: "Commencer",
    
    // Auth Page
    signIn: "Se connecter",
    signUp: "S'inscrire",
    createAccount: "Créer un compte",
    signingIn: "Connexion...",
    creatingAccount: "Création du compte...",
    accountCreated: "Compte créé! Vous pouvez maintenant vous connecter.",
    trackInventory: "Suivez l'inventaire, réduisez le gaspillage, optimisez votre cuisine",
    password: "Mot de passe",
    fullNameLabel: "Nom complet",
    
    // Affiliates Page
    affiliateProgram: "Programme d'affiliation",
    inviteRestaurants: "Invitez des restaurants et gagnez des récompenses pour chaque parrainage réussi",
    totalReferrals: "Parrainages totaux",
    totalEarnings: "Gains totaux",
    pending: "En attente",
    awaitingSignup: "En attente d'inscription",
    commissionEarned: "Commission gagnée",
    inviteRestaurant: "Inviter un restaurant",
    sendInvitationEarn: "Envoyez une invitation et gagnez une commission lorsqu'ils rejoignent",
    restaurantEmail: "Email du restaurant",
    commissionStructure: "Structure de commission",
    firstYearCommission: "20% de l'abonnement de la première année",
    recurringCommission: "10% récurrent à vie",
    bonusActive: "Bonus pour les restaurants actifs",
    sendInvitation: "Envoyer l'invitation",
    yourReferrals: "Vos parrainages",
    trackInvitedRestaurants: "Suivez vos restaurants invités",
    noReferralsYet: "Aucun parrainage pour le moment",
    startInviting: "Commencez à inviter des restaurants pour gagner des récompenses!",
    invited: "Invité",
    earned: "gagné",
    pleaseEnterEmail: "Veuillez entrer une adresse email",
    invitationSent: "Invitation envoyée avec succès!",
    active: "actif",
    
    // Dashboard Page (partial - add if needed)
    analyticsDashboard: "Tableau de bord analytique",
    comprehensiveInsights: "Aperçu complet de votre cycle d'inventaire quotidien",
    avgDailyWaste: "Gaspillage quotidien moyen",
    accuracy: "Précision",
    monthlySavings: "Économies mensuelles",
    efficiency: "Efficacité",
    activeItems: "Articles actifs",
    wasteReduction: "Réduction du gaspillage",
    
    // Containers Page
    containerManagement: "Gestion des conteneurs",
    threePhaseWorkflow: "Flux de travail en trois phases: Matin → Entre-deux → Soir",
    whyThisProcess: "Pourquoi ce processus?",
    startMorning: "Commencer le matin",
    duringService: "Pendant le service",
    viewGuide: "Voir le guide",
    startEvening: "Commencer le soir",
    backToSelection: "← Retour à la sélection",
    dailyWorkflowComplete: "Flux de travail quotidien terminé! 🎉",
    failedToLoadContainers: "Échec du chargement des conteneurs",
    
    // Inventory Page
    monitorStockLevels: "Surveillez vos niveaux de stock d'ingrédients et gérez l'inventaire",
    loadingInventory: "Chargement de l'inventaire...",
    noInventoryItems: "Aucun article d'inventaire trouvé",
    lowStock: "Stock faible",
    inStock: "En stock",
    currentStock: "Stock actuel",
    minStock: "Stock minimum",
    lastOrder: "Dernière commande",
    costTrend: "Tendance des coûts",
    supplier: "Fournisseur",
    
    // Menu Management Page
    menuManagement: "Gestion du menu",
    setupRestaurantMenu: "Configurez le menu de votre restaurant et assignez les ingrédients",
    addMenuItem: "Ajouter un élément de menu",
    editMenuItem: "Modifier l'élément de menu",
    addNewMenuItem: "Ajouter un nouvel élément de menu",
    enterMenuItemDetails: "Entrez les détails de votre élément de menu",
    itemName: "Nom de l'article",
    category: "Catégorie",
    description: "Description",
    cancel: "Annuler",
    add: "Ajouter",
    update: "Mettre à jour",
    item: "Article",
    menuItemsCount: "Éléments de menu",
    manageMenuItems: "Gérez les éléments de menu de votre restaurant",
    loadingMenuItems: "Chargement des éléments de menu...",
    noMenuItemsYet: "Aucun élément de menu pour le moment",
    addFirstMenuItem: "Ajoutez votre premier élément de menu",
    menuItemAdded: "Élément de menu ajouté avec succès!",
    menuItemUpdated: "Élément de menu mis à jour avec succès!",
    menuItemDeleted: "Élément de menu supprimé avec succès!",
    confirmDelete: "Êtes-vous sûr de vouloir supprimer cet élément de menu?",
    failedToLoadMenuItems: "Échec du chargement des éléments de menu",
    
    // Subscription Page
    subscriptionTitle: "Abonnement",
    manageMembership: "Gérez votre abonnement BacSense Pro",
    currentSubscriptionStatus: "Statut d'abonnement actuel",
    activeSubscription: "Abonnement actif",
    dayTrial: "Essai de 15 jours",
    noActiveSubscription: "Aucun abonnement actif",
    subscribeToAccess: "Abonnez-vous pour accéder à toutes les fonctionnalités",
    trialEnds: "L'essai se termine:",
    nextBillingDate: "Prochaine date de facturation:",
    refreshStatus: "Actualiser le statut",
    manageSubscription: "Gérer l'abonnement",
    subscribeNow: "S'abonner maintenant",
    opening: "Ouverture...",
    processing: "Traitement...",
    bacSenseProMembership: "Abonnement BacSense Pro",
    monthlyPrice: "199€/mois avec 15 jours d'essai gratuit",
    subscriptionActivated: "Abonnement activé avec succès!",
    checkoutCanceled: "Paiement de l'abonnement annulé",
    
    // 404 Page
    pageNotFound: "Page non trouvée",
    oops: "Oups!",
    returnHome: "Retour à l'accueil",
    
    // Container workflow
    morningLog: "Journal du matin",
    eveningLog: "Journal du soir",
    inBetweenTracking: "Suivi pendant le service",
    fillAllFields: "Veuillez remplir tous les champs requis",
    
    // Business Setup Page
    businessTitle: "Entreprise",
    configureRestaurantDetails: "Configurez les détails opérationnels de votre restaurant pour des insights alimentés par l'IA",
    operatingHours: "Heures d'ouverture",
    setDailySchedule: "Définissez votre planning quotidien",
    openingTime: "Heure d'ouverture",
    closingTime: "Heure de fermeture",
    timezone: "Fuseau horaire",
    peakServiceHours: "Heures de pointe",
    defineBusyPeriods: "Définissez vos périodes de repas les plus chargées pour de meilleures prévisions IA",
    lunchRush: "Service du midi",
    dinnerRush: "Service du soir",
    restaurantDetails: "Détails du restaurant",
    aboutEstablishment: "Informations sur votre établissement",
    cuisineType: "Type de cuisine",
    cuisinePlaceholder: "ex: Italienne, Française, Fusion Asiatique",
    averageDailyCovers: "Couverts quotidiens moyens",
    coversPlaceholder: "ex: 120",
    typicalCustomers: "Nombre typique de clients servis par jour",
    saveSettings: "Enregistrer les paramètres",
    businessSettingsUpdated: "Paramètres d'entreprise mis à jour avec succès",
  },
  es: {
    // Navigation
    dashboard: "Panel",
    containers: "Contenedores",
    menu: "Menú",
    ingredients: "Ingredientes",
    inventory: "Inventario",
    analytics: "Analíticas",
    dishAnalytics: "Analíticas de platos",
    leaderboard: "Clasificación",
    affiliates: "Afiliados",
    team: "Equipo",
    recipes: "Recetas",
    businessSetup: "Negocio",
    subscription: "Suscripción",
    settings: "Configuración",
    signOut: "Cerrar sesión",
    
    // Settings Page
    profile: "Perfil",
    integrations: "Integraciones",
    notifications: "Notificaciones",
    security: "Seguridad",
    preferences: "Preferencias",
    language: "Idioma",
    theme: "Tema",
    lightMode: "Claro",
    darkMode: "Oscuro",
    systemMode: "Sistema",
    english: "English",
    french: "Français",
    spanish: "Español",
    manageAccountPreferences: "Administre su cuenta y preferencias",
    profileInformation: "Información de perfil",
    updatePersonalDetails: "Actualice sus datos personales",
    fullName: "Nombre completo",
    yourName: "Su nombre",
    email: "Email",
    emailCannotChange: "El email no se puede cambiar directamente",
    saveChanges: "Guardar cambios",
    saving: "Guardando...",
    appearanceLanguage: "Apariencia e idioma",
    customizeExperience: "Personalice su experiencia",
    externalIntegrations: "Integraciones externas",
    connectRestaurantSystems: "Conecte BacSense con sus sistemas de gestión para sincronización automática",
    restaurantManagementPos: "Gestión de restaurante y TPV",
    notConnected: "No conectado",
    apiKey: "Clave API",
    enterApiKey: "Ingrese su clave API de {service}",
    salesOrderSync: "Sincronización de ventas y pedidos",
    restaurantManagementPlatform: "Plataforma de gestión de restaurante",
    deliveryOrderTracking: "Seguimiento de pedidos de entrega",
    saveIntegrationSettings: "Guardar configuración de integraciones",
    manageNotificationPreferences: "Administre sus preferencias de notificación",
    dailyReminders: "Recordatorios diarios",
    morningEveningReminders: "Recordatorios de registro matutino y nocturno",
    wasteAlerts: "Alertas de desperdicio",
    wasteExceedsTargets: "Reciba notificaciones cuando el desperdicio supere los objetivos",
    competitionUpdates: "Actualizaciones de competencia",
    teamLeaderboardChanges: "Cambios en la clasificación del equipo",
    affiliateNews: "Noticias de afiliados",
    referralCommissionUpdates: "Actualizaciones sobre sus referencias y comisiones",
    manageAccountSecurity: "Administre la seguridad de su cuenta",
    changePassword: "Cambiar contraseña",
    profileUpdatedSuccess: "¡Perfil actualizado exitosamente!",
    notificationPreferencesUpdated: "Preferencias de notificación actualizadas",
    
    // Index/Landing Page
    appTitle: "BacSense",
    appDescription: "Seguimiento y análisis de desperdicio de alimentos impulsado por IA para restaurantes",
    getStarted: "Comenzar",
    
    // Auth Page
    signIn: "Iniciar sesión",
    signUp: "Registrarse",
    createAccount: "Crear cuenta",
    signingIn: "Iniciando sesión...",
    creatingAccount: "Creando cuenta...",
    accountCreated: "¡Cuenta creada! Ahora puede iniciar sesión.",
    trackInventory: "Siga el inventario, reduzca el desperdicio, optimice su cocina",
    password: "Contraseña",
    fullNameLabel: "Nombre completo",
    
    // Affiliates Page
    affiliateProgram: "Programa de afiliados",
    inviteRestaurants: "Invite restaurantes y gane recompensas por cada referido exitoso",
    totalReferrals: "Referencias totales",
    totalEarnings: "Ganancias totales",
    pending: "Pendiente",
    awaitingSignup: "Esperando registro",
    commissionEarned: "Comisión ganada",
    inviteRestaurant: "Invitar un restaurante",
    sendInvitationEarn: "Envíe una invitación y gane comisión cuando se unan",
    restaurantEmail: "Email del restaurante",
    commissionStructure: "Estructura de comisión",
    firstYearCommission: "20% del primer año de suscripción",
    recurringCommission: "10% recurrente de por vida",
    bonusActive: "Bonificación por restaurantes activos",
    sendInvitation: "Enviar invitación",
    yourReferrals: "Sus referencias",
    trackInvitedRestaurants: "Rastree sus restaurantes invitados",
    noReferralsYet: "Aún no hay referencias",
    startInviting: "¡Comience a invitar restaurantes para ganar recompensas!",
    invited: "Invitado",
    earned: "ganado",
    pleaseEnterEmail: "Por favor ingrese una dirección de email",
    invitationSent: "¡Invitación enviada con éxito!",
    active: "activo",
    
    // Dashboard Page
    analyticsDashboard: "Panel de análisis",
    comprehensiveInsights: "Información completa sobre su ciclo de inventario diario",
    avgDailyWaste: "Desperdicio diario promedio",
    accuracy: "Precisión",
    monthlySavings: "Ahorros mensuales",
    efficiency: "Eficiencia",
    activeItems: "Artículos activos",
    wasteReduction: "Reducción de desperdicio",
    
    // Containers Page
    containerManagement: "Gestión de contenedores",
    threePhaseWorkflow: "Flujo de trabajo en tres fases: Mañana → Intermedio → Noche",
    whyThisProcess: "¿Por qué este proceso?",
    startMorning: "Iniciar mañana",
    duringService: "Durante el servicio",
    viewGuide: "Ver guía",
    startEvening: "Iniciar noche",
    backToSelection: "← Volver a selección",
    dailyWorkflowComplete: "¡Flujo de trabajo diario completado! 🎉",
    failedToLoadContainers: "Error al cargar contenedores",
    
    // Inventory Page
    monitorStockLevels: "Supervise sus niveles de stock de ingredientes y administre el inventario",
    loadingInventory: "Cargando inventario...",
    noInventoryItems: "No se encontraron artículos de inventario",
    lowStock: "Stock bajo",
    inStock: "En stock",
    currentStock: "Stock actual",
    minStock: "Stock mínimo",
    lastOrder: "Último pedido",
    costTrend: "Tendencia de costos",
    supplier: "Proveedor",
    
    // Menu Management Page
    menuManagement: "Gestión de menú",
    setupRestaurantMenu: "Configure el menú de su restaurante y asigne ingredientes",
    addMenuItem: "Agregar elemento de menú",
    editMenuItem: "Editar elemento de menú",
    addNewMenuItem: "Agregar nuevo elemento de menú",
    enterMenuItemDetails: "Ingrese los detalles de su elemento de menú",
    itemName: "Nombre del artículo",
    category: "Categoría",
    description: "Descripción",
    cancel: "Cancelar",
    add: "Agregar",
    update: "Actualizar",
    item: "Artículo",
    menuItemsCount: "Elementos de menú",
    manageMenuItems: "Administre los elementos de menú de su restaurante",
    loadingMenuItems: "Cargando elementos de menú...",
    noMenuItemsYet: "Aún no hay elementos de menú",
    addFirstMenuItem: "Agregue su primer elemento de menú",
    menuItemAdded: "¡Elemento de menú agregado con éxito!",
    menuItemUpdated: "¡Elemento de menú actualizado con éxito!",
    menuItemDeleted: "¡Elemento de menú eliminado con éxito!",
    confirmDelete: "¿Está seguro de que desea eliminar este elemento de menú?",
    failedToLoadMenuItems: "Error al cargar elementos de menú",
    
    // Subscription Page
    subscriptionTitle: "Suscripción",
    manageMembership: "Administre su membresía BacSense Pro",
    currentSubscriptionStatus: "Estado de suscripción actual",
    activeSubscription: "Suscripción activa",
    dayTrial: "Prueba de 15 días",
    noActiveSubscription: "Sin suscripción activa",
    subscribeToAccess: "Suscríbase para acceder a todas las funciones",
    trialEnds: "La prueba termina:",
    nextBillingDate: "Próxima fecha de facturación:",
    refreshStatus: "Actualizar estado",
    manageSubscription: "Administrar suscripción",
    subscribeNow: "Suscribirse ahora",
    opening: "Abriendo...",
    processing: "Procesando...",
    bacSenseProMembership: "Membresía BacSense Pro",
    monthlyPrice: "€199/mes con 15 días de prueba gratuita",
    subscriptionActivated: "¡Suscripción activada con éxito!",
    checkoutCanceled: "Pago de suscripción cancelado",
    
    // 404 Page
    pageNotFound: "Página no encontrada",
    oops: "¡Ups!",
    returnHome: "Volver al inicio",
    
    // Container workflow
    morningLog: "Registro matutino",
    eveningLog: "Registro nocturno",
    inBetweenTracking: "Seguimiento durante el servicio",
    fillAllFields: "Por favor complete todos los campos requeridos",
    
    // Business Setup Page
    businessTitle: "Negocio",
    configureRestaurantDetails: "Configure los detalles operacionales de su restaurante para insights impulsados por IA",
    operatingHours: "Horario de operación",
    setDailySchedule: "Establezca su horario diario",
    openingTime: "Hora de apertura",
    closingTime: "Hora de cierre",
    timezone: "Zona horaria",
    peakServiceHours: "Horas pico del servicio",
    defineBusyPeriods: "Defina sus períodos de comida más ocupados para mejores predicciones de IA",
    lunchRush: "Servicio del almuerzo",
    dinnerRush: "Servicio de la cena",
    restaurantDetails: "Detalles del restaurante",
    aboutEstablishment: "Información sobre su establecimiento",
    cuisineType: "Tipo de cocina",
    cuisinePlaceholder: "ej: Italiana, Francesa, Fusión Asiática",
    averageDailyCovers: "Comensales diarios promedio",
    coversPlaceholder: "ej: 120",
    typicalCustomers: "Número típico de clientes atendidos por día",
    saveSettings: "Guardar configuración",
    businessSettingsUpdated: "Configuración del negocio actualizada exitosamente",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("language");
    return (stored as Language) || "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
